import { useState, useEffect, useCallback, useRef } from "react";
import DashboardLayout from "../../common/DashboardLayout";
import { Link } from "react-router-dom";
import AdminServices from "../AdminServices";
import $ from "jquery";
import "datatables.net-dt/css/dataTables.dataTables.min.css";
import "datatables.net";
import { Trash2, Settings } from "react-feather";
import TeacherServices from "../TeacherServices";
import "./GrantApplicationList.css";

const GrantApplications = () => {
  const [grantApplications, setGrantApplications] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isDeleteConfirmPopup, setIsDeleteConfirmPopup] = useState(false);
  const [confirmedDeleteUserId, setConfirmedDeleteUserId] = useState("");
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  /**
   * Which columns a reviewer sees by default.
   *
   * "Tell us why your school should be considered" was hidden, as were the two
   * emergency-plan answers and the applicant's title. Those are the fields a
   * grant decision actually turns on, so the screen opened showing contact
   * details and withholding the application. Someone reading an entry got a name
   * and a phone number and had to know a column picker existed to see the rest.
   */
  const DEFAULT_COLUMNS = {
    action: true,
    index: true,
    applicantName: true,
    schoolName: true,
    positionTitle: false,
    phone: true,
    email: true,
    appliedOn: true,
    streetAddress: false,
    city: false,
    state: true,
    typeOfFacility: true,
    numberOfStaff: true,
    pastTraining: true,
    eop: true,
    message: true
  };

  const COLUMN_PREFS_KEY = 'grantApplicationColumns';

  /**
   * Choices were plain component state, so every visit and every navigation back
   * reset them - a reviewer who opened a column had to open it again each time.
   * Unknown keys from an older saved shape are ignored rather than trusted, so a
   * stale entry cannot resurrect a column that no longer exists.
   */
  const loadColumnPrefs = () => {
    try {
      const raw = localStorage.getItem(COLUMN_PREFS_KEY);
      if (!raw) return DEFAULT_COLUMNS;
      const saved = JSON.parse(raw);
      if (!saved || typeof saved !== 'object') return DEFAULT_COLUMNS;
      const merged = { ...DEFAULT_COLUMNS };
      Object.keys(DEFAULT_COLUMNS).forEach((k) => {
        if (typeof saved[k] === 'boolean') merged[k] = saved[k];
      });
      // These two are the only way back to the rows; never let a saved state
      // leave the reviewer with no actions and no row numbers.
      merged.action = true;
      merged.index = true;
      return merged;
    } catch (e) {
      return DEFAULT_COLUMNS;
    }
  };

  const [visibleColumns, setVisibleColumns] = useState(loadColumnPrefs);

  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(visibleColumns));
    } catch (e) { /* a full or blocked store is not worth breaking the page over */ }
  }, [visibleColumns]);

  const dataTableRef = useRef(null);

  const fetchGrantApplications = useCallback(async () => {
    try {
      // Backend returns ALL applications (no pagination), but we'll handle pagination on frontend
      const response = await AdminServices.getAllGrantApplications();

      const allApplications = response.grantApplications || []; // Ensure this is an array

      // Apply search filter if provided
      let filteredApplications = allApplications;
      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        filteredApplications = allApplications.filter(app =>
          app.applicant_name?.toLowerCase().includes(searchLower) ||
          app.center_school_name?.toLowerCase().includes(searchLower) ||
          app.email?.toLowerCase().includes(searchLower) ||
          app.phone?.includes(search)
        );
      }

      // Sort by Oldest First so that the Index # corresponds to chronological order (1 = Oldest)
      filteredApplications.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      // DataTables handles pagination internally, so pass all filtered applications
      setGrantApplications(filteredApplications);
      setTotalPages(1); // DataTables handles pagination, so we don't need to track pages

      // Calculate daily statistics from ALL applications (not filtered by search)
      const applications = allApplications;

      // Calculate daily statistics from ALL applications
      const dailyStatsMap = {};
      applications.forEach(application => {
        if (!application.createdAt) return; // Skip if no date

        // Parse the date properly - use local date, not UTC, to avoid timezone shifts
        let dateStr;
        try {
          let date;
          if (typeof application.createdAt === 'string') {
            // If it's already a date string (YYYY-MM-DD), use it directly
            if (/^\d{4}-\d{2}-\d{2}/.test(application.createdAt)) {
              dateStr = application.createdAt.split('T')[0];
            } else {
              // Parse and convert to local date string
              date = new Date(application.createdAt);
              if (isNaN(date.getTime())) {
                console.warn('Invalid date:', application.createdAt);
                return;
              }
              // Use local date components to avoid timezone issues
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              dateStr = `${year}-${month}-${day}`;
            }
          } else if (application.createdAt instanceof Date) {
            date = application.createdAt;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            dateStr = `${year}-${month}-${day}`;
          } else {
            // Try to parse as date
            date = new Date(application.createdAt);
            if (isNaN(date.getTime())) {
              console.warn('Invalid date:', application.createdAt);
              return;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            dateStr = `${year}-${month}-${day}`;
          }
        } catch (error) {
          console.warn('Error parsing date:', application.createdAt, error);
          return;
        }

        if (!dailyStatsMap[dateStr]) {
          dailyStatsMap[dateStr] = { date: dateStr, totalApplications: 0, uniqueParticipants: new Set() };
        }
        dailyStatsMap[dateStr].totalApplications++;
        if (application.email && application.email.trim()) {
          dailyStatsMap[dateStr].uniqueParticipants.add(application.email.toLowerCase().trim());
        }
      });

      // Convert to array and sort by date (newest first), then take last 5 days
      const dailyStatsArray = Object.values(dailyStatsMap)
        .map(stat => ({
          ...stat,
          uniqueParticipants: stat.uniqueParticipants.size
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 5); // Last 5 days

      // Debug: Log applications for Nov 25 to verify counting
      const nov25Apps = applications.filter(app => {
        if (!app.createdAt) return false;
        const date = new Date(app.createdAt);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}` === '2024-11-25';
      });
      console.log('Nov 25 applications:', nov25Apps.length, nov25Apps.map(a => ({
        id: a._id,
        createdAt: a.createdAt,
        email: a.email
      })));

      console.log('Daily stats calculated:', dailyStatsArray);
      console.log('Total applications processed:', applications.length);
      setDailyStats(dailyStatsArray);
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  }, [currentPage, search]); // Include currentPage and search as dependencies

  const handleDeleteData = async () => {
    try {
      await AdminServices.deleteGrantApplication(confirmedDeleteUserId);

      // Optimistically update state to remove the deleted item immediately
      // This ensures the UI reflects the change even if the re-fetch is delayed
      setGrantApplications(prev => prev.filter(app => app._id !== confirmedDeleteUserId));

      // We don't need to manually destroy the DataTable here because:
      // 1. We just updated the state (setGrantApplications)
      // 2. The useEffect hook on line 362 listens for [grantApplications] changes
      // 3. That useEffect already handles destroying and re-initializing the DataTable

      setIsDeleteConfirmPopup(false);
      setConfirmedDeleteUserId("");

      // Still fetch from backend to ensure consistency
      await fetchGrantApplications();
    } catch (error) {
      console.error("Failed to delete grant application:", error);
      setIsDeleteConfirmPopup(false);
      setConfirmedDeleteUserId("");
    }
  }

  useEffect(() => {
    fetchGrantApplications();
  }, [fetchGrantApplications]); // fetchGrantApplications now has stable dependencies

  useEffect(() => {
    // Always destroy existing DataTable before creating a new one
    if ($.fn.DataTable.isDataTable("#grantApplicationsTable")) {
      try {
        const table = $("#grantApplicationsTable").DataTable();
        table.destroy(); // Keep the DOM elements (React handles them)
      } catch (error) {
        console.warn("Error destroying DataTable:", error);
      }
    }

    // If no data, don't initialize DataTable
    if (grantApplications.length === 0) {
      return;
    }

    // Add a small delay to ensure DOM is ready after state update
    const initTimeout = setTimeout(() => {

      // Define column widths as constants for consistency
      const columnWidths = [
        '70px',  // Action
        '72px',  // Index (grant # column) — fits 6 digits without wrapping (future growth)
        '120px', // Applicant Name
        '140px', // Center/School Name
        '130px', // Position/Title
        '120px', // Phone
        '200px', // Email
        '130px', // Applied On
        '150px', // Street Address
        '120px', // City
        '80px',  // State
        '150px', // Type of Facility
        '120px', // Number of Staff
        '180px', // Past Training Type
        '150px', // EOP
        '200px'  // Message
      ];

      const table = $("#grantApplicationsTable").DataTable({
        responsive: false,
        autoWidth: false,
        scrollX: true,
        scrollCollapse: false,
        paging: true,
        searching: true,
        ordering: true,
        pageLength: 25,
        lengthMenu: [[25, 50, -1], [25, 50, "All"]],
        fixedColumns: false,
        order: [[1, 'desc']], // Default sort by Number column (index 1) descending (Newest/Highest # First)
        columnDefs: [
          { targets: 0, width: columnWidths[0], visible: visibleColumns.action, className: 'text-left' }, // Action - widened
          { targets: 1, width: columnWidths[1], visible: visibleColumns.index, className: 'text-left' }, // Index
          { targets: 2, width: columnWidths[2], visible: visibleColumns.applicantName, className: 'text-wrap text-left' }, // Applicant Name - narrowed
          { targets: 3, width: columnWidths[3], visible: visibleColumns.schoolName, className: 'text-wrap text-left' }, // Center/School Name - narrowed
          { targets: 4, width: columnWidths[4], visible: visibleColumns.positionTitle, className: 'text-left' }, // Position/Title
          { targets: 5, width: columnWidths[5], visible: visibleColumns.phone, className: 'text-left' }, // Phone
          { targets: 6, width: columnWidths[6], visible: visibleColumns.email, className: 'text-left' }, // Email
          {
            targets: 7,
            width: columnWidths[7],
            visible: visibleColumns.appliedOn,
            className: 'text-wrap text-left',
            type: 'date', // Tell DataTables this is a date column
            orderData: [7] // Use the data-order attribute for sorting
          }, // Applied On - narrowed
          { targets: 8, width: columnWidths[8], visible: visibleColumns.streetAddress, className: 'text-wrap text-left' }, // Street Address - narrowed
          { targets: 9, width: columnWidths[9], visible: visibleColumns.city, className: 'text-left' }, // City
          { targets: 10, width: columnWidths[10], visible: visibleColumns.state, className: 'text-left' }, // State
          { targets: 11, width: columnWidths[11], visible: visibleColumns.typeOfFacility, className: 'text-left' }, // Type of Facility
          { targets: 12, width: columnWidths[12], visible: visibleColumns.numberOfStaff, className: 'text-left' }, // Number of Staff
          { targets: 13, width: columnWidths[13], visible: visibleColumns.pastTraining, className: 'text-left' }, // Past Training Type
          { targets: 14, width: columnWidths[14], visible: visibleColumns.eop, className: 'text-left' }, // EOP
          { targets: 15, width: columnWidths[15], visible: visibleColumns.message, className: 'text-left' } // Message
        ]
      });

      dataTableRef.current = table;
      $('#grantApplicationsTable').closest('.dt-layout-table').addClass('dt-custom-table');

      // Function to realign columns - use configured widths consistently
      const realignColumns = () => {
        // Wait for DOM to update
        requestAnimationFrame(() => {
          // First adjust columns
          table.columns.adjust();

          // Wait a bit more for adjustments to take effect
          setTimeout(() => {
            // Apply configured widths to all visible columns
            table.columns().every(function () {
              const column = this;
              const columnIndex = column.index();
              const header = $(column.header());

              // Only process visible columns
              if (header.is(':visible') && columnWidths[columnIndex]) {
                const targetWidth = parseInt(columnWidths[columnIndex].replace('px', ''));

                // Set width on header
                header.css({
                  'width': targetWidth + 'px',
                  'min-width': targetWidth + 'px',
                  'max-width': targetWidth + 'px',
                  'text-align': 'left',
                  'padding-left': '8px',
                  'padding-right': '10px'
                });

                // Set matching width on all cells in this column
                column.nodes().to$().each(function () {
                  $(this).css({
                    'width': targetWidth + 'px',
                    'min-width': targetWidth + 'px',
                    'max-width': targetWidth + 'px'
                  });
                });
              }
            });

            // Force left alignment on all visible headers
            $('#grantApplicationsTable thead th:visible').css({
              'text-align': 'left',
              'padding-left': '8px',
              'padding-right': '10px'
            });

            // Force table to recalculate layout
            table.columns.adjust();
            table.draw(false);
          }, 100);
        });
      };

      // Initial alignment
      setTimeout(realignColumns, 300);

      // Re-align whenever columns visibility changes
      table.on('column-visibility.dt', function () {
        realignColumns();
      });

      // Also re-align on draw events
      table.on('draw.dt', function () {
        setTimeout(() => {
          $('#grantApplicationsTable thead th:visible').each(function () {
            $(this).css({
              'text-align': 'left',
              'padding-left': '8px',
              'padding-right': '10px'
            });
          });
        }, 50);
      });
    }, 50); // Close the setTimeout for initTimeout

    // Cleanup function
    return () => {
      clearTimeout(initTimeout);
      if ($.fn.DataTable.isDataTable("#grantApplicationsTable")) {
        try {
          $("#grantApplicationsTable").DataTable().destroy();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, [grantApplications, visibleColumns]);

  // Close column settings when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnSettings && !event.target.closest('.grant-table-header-controls')) {
        setShowColumnSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnSettings]);

  const toggleColumn = (columnKey, columnIndex) => {
    setVisibleColumns(prev => {
      const newState = { ...prev, [columnKey]: !prev[columnKey] };
      // DataTables will update automatically via the useEffect dependency
      return newState;
    });
  };

  const showAllColumns = () => {
    setVisibleColumns({
      action: true,
      index: true,
      applicantName: true,
      schoolName: true,
      positionTitle: true,
      phone: true,
      email: true,
      appliedOn: true,
      streetAddress: true,
      city: true,
      state: true,
      typeOfFacility: true,
      numberOfStaff: true,
      pastTraining: true,
      eop: true,
      message: true
    });
  };

  const hideAllColumns = () => {
    setVisibleColumns({
      action: true, // Keep action column visible
      index: true, // Keep index column visible
      applicantName: false,
      schoolName: false,
      positionTitle: false,
      phone: false,
      email: false,
      appliedOn: false,
      streetAddress: false,
      city: false,
      state: false,
      typeOfFacility: false,
      numberOfStaff: false,
      pastTraining: false,
      eop: false,
      message: false
    });
  };

  const resetToDefault = () => {
    setVisibleColumns({ ...DEFAULT_COLUMNS });
  };

  return (
    <DashboardLayout
      role="admin"
      layoutType="topnav"
      activeMenu="Grant Applications"
      showBreadcrumbs={false}
    >
      <div className={`admincontent ${sidebarVisible ? 'col-xl-12 col-lg-12' : 'col-12'}`}>
        {/* /Dashboard Grid */}
        <div className="row">
          {/* Daily Statistics */}
          {dailyStats && dailyStats.length > 0 && (
            <div className="col-12 mb-3">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title mb-3">Daily Statistics</h5>
                  <div className="row">
                    {dailyStats.map((day, index) => {
                      // Color schemes for each card (cycling through different colors)
                      const colorSchemes = [
                        { gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#ffffff' },
                        { gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', text: '#ffffff' },
                        { gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', text: '#ffffff' },
                        { gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', text: '#1a1a1a' },
                        { gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', text: '#1a1a1a' }
                      ];
                      const colorScheme = colorSchemes[index % colorSchemes.length];

                      return (
                        <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-2-4 mb-3">
                          <div
                            className="card border-0 daily-stat-card"
                            style={{
                              padding: '12px',
                              height: '100%',
                              background: colorScheme.gradient,
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
                              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 8px 12px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)';
                            }}
                          >
                            <div className="card-body p-2">
                              <h6
                                className="card-subtitle mb-2"
                                style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: colorScheme.text
                                }}
                              >
                                {new Date(day.date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </h6>
                              <p
                                className="mb-1"
                                style={{
                                  fontSize: '13px',
                                  color: colorScheme.text,
                                  opacity: 0.95,
                                  margin: '4px 0'
                                }}
                              >
                                {day.totalApplications} total applications
                              </p>
                              <p
                                className="mb-0"
                                style={{
                                  fontSize: '13px',
                                  color: colorScheme.text,
                                  opacity: 0.95,
                                  margin: '4px 0'
                                }}
                              >
                                {day.uniqueParticipants} unique participants
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Course Grid */}
          <div className="custom-table">
            <div className="grant-table-header-wrapper">
              <div className="grant-table-header-title">
                <h3>Grant Applications</h3>
              </div>
              <div className="grant-table-header-controls">
                <button
                  className="column-settings-btn"
                  onClick={() => setShowColumnSettings(!showColumnSettings)}
                  title="Column Settings"
                >
                  <Settings size={18} />
                  <span>Columns</span>
                </button>
                {showColumnSettings && (
                  <div className="column-settings-panel">
                    <h4>Show/Hide Columns</h4>
                    <div className="column-settings-actions">
                      <button className="column-action-btn" onClick={showAllColumns}>
                        Show All
                      </button>
                      <button className="column-action-btn" onClick={hideAllColumns}>
                        Hide All
                      </button>
                      <button className="column-action-btn" onClick={resetToDefault}>
                        Default
                      </button>
                    </div>
                    <div className="column-checkboxes">
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.action}
                          onChange={() => toggleColumn('action', 0)}
                        />
                        Action
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.index}
                          onChange={() => toggleColumn('index', 1)}
                        />
                        Index
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.applicantName}
                          onChange={() => toggleColumn('applicantName', 2)}
                        />
                        Applicant Name
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.schoolName}
                          onChange={() => toggleColumn('schoolName', 3)}
                        />
                        School Name
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.positionTitle}
                          onChange={() => toggleColumn('positionTitle', 4)}
                        />
                        Position/Title
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.phone}
                          onChange={() => toggleColumn('phone', 5)}
                        />
                        Phone
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.email}
                          onChange={() => toggleColumn('email', 6)}
                        />
                        Email
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.appliedOn}
                          onChange={() => toggleColumn('appliedOn', 7)}
                        />
                        Applied On
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.streetAddress}
                          onChange={() => toggleColumn('streetAddress', 8)}
                        />
                        Street Address
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.city}
                          onChange={() => toggleColumn('city', 9)}
                        />
                        City
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.state}
                          onChange={() => toggleColumn('state', 10)}
                        />
                        State
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.typeOfFacility}
                          onChange={() => toggleColumn('typeOfFacility', 11)}
                        />
                        Type of Facility
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.numberOfStaff}
                          onChange={() => toggleColumn('numberOfStaff', 12)}
                        />
                        Number of Staff
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.pastTraining}
                          onChange={() => toggleColumn('pastTraining', 13)}
                        />
                        Past Training Type
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.eop}
                          onChange={() => toggleColumn('eop', 14)}
                        />
                        EOP
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns.message}
                          onChange={() => toggleColumn('message', 15)}
                        />
                        Message
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <table
              id="grantApplicationsTable"
              className="table table-nowrap mb-0"
            >
              <thead>
                <tr>
                  <th>Action</th>
                  <th>#</th>
                  <th>Applicant<br />Name</th>
                  <th>Center/<br />School Name</th>
                  <th>Position/<br />Title</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Applied<br />On</th>
                  <th>Street<br />Address</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Type of<br />Facility</th>
                  <th>Number of<br />Staff</th>
                  <th>Past Training<br />Type</th>
                  <th>EOP</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {grantApplications.map((grantApplication, index) => (
                  <tr key={grantApplication._id}>
                    <td>
                      <Trash2 className="delete-data" onClick={() => {
                        setConfirmedDeleteUserId(grantApplication._id);
                        setIsDeleteConfirmPopup(true);
                      }} />
                    </td>
                    <td>{index + 1}</td>
                    <td>
                      {grantApplication.applicant_name}
                    </td>
                    <td>
                      {grantApplication.center_school_name}
                    </td>
                    <td>{grantApplication.position_title}</td>
                    <td>{grantApplication.phone}</td>
                    <td>{grantApplication.email}</td>
                    <td className="applied-on-cell" data-order={new Date(grantApplication.createdAt).getTime()}>
                      <div className="applied-date">{new Date(grantApplication.createdAt).toLocaleDateString()}</div>
                      <div className="applied-time">{new Date(grantApplication.createdAt).toLocaleTimeString()}</div>
                    </td>
                    <td>{grantApplication.street_address}</td>
                    <td>{grantApplication.city}</td>
                    <td>{grantApplication.state}</td>
                    <td>{grantApplication.type_of_facilities.map((e, index) => (<span key={index}>{e}<br /></span>))}</td>
                    <td>{grantApplication.number_of_staff}</td>
                    <td>
                      <div className="grant-text-cell">
                        {grantApplication.has_emergency_training ? grantApplication.training_type : ""}
                      </div>
                    </td>
                    <td>
                      <div className="grant-text-cell">
                        {grantApplication.have_emergency_plan ? grantApplication.emergency_plans.map((e, index) => (<span key={index}>{e}<br /></span>)) : ""}
                      </div>
                    </td>
                    <td className="grant-message">
                      <div className="grant-text-cell">
                        {grantApplication.message}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* <div className="dash-pagination">
                  <div className="row align-items-center">
                    <div className="col-6">
                      <p>Page 1 of 2</p>
                    </div>
                    <div className="col-6">
                      <ul className="pagination">
                        <li className="active">
                          <Link to="#">1</Link>
                        </li>
                        <li>
                          <Link to="#">2</Link>
                        </li>
                        <li>
                          <Link to="#">
                            <i className="bx bx-chevron-right" />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div> */}
      </div>
      {isDeleteConfirmPopup ?
        (<div
          className="delete-confirm-popup"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: 16,
          }}
        >
          <div
            className="popup-content"
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: 16,
              maxWidth: 420,
              width: "100%",
              boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
            }}
          >
            <p style={{ marginBottom: 12 }}>Are you sure you want to delete this application?</p>
            <div className="popup-buttons" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-danger" onClick={handleDeleteData}>Yes, delete</button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setConfirmedDeleteUserId("");
                  setIsDeleteConfirmPopup(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>)
        :
        <></>
      }
    </DashboardLayout>
  );
};

export default GrantApplications;
